import { useRef, useState } from 'react';
import { can } from '../auth/store';
import { PersonAvatar } from './PersonAvatar';
import { ConfirmDialog } from './ui';
import { useUploadPersonPhoto, useDeletePersonPhoto } from '../api/queries/persons';

const ACCEPT = 'image/png,image/jpeg,image/webp';

// Person photo with management controls. Shows the avatar (large) plus
// Upload / Replace / Remove, gated on person.write. Mirrors the
// DocumentAttachment upload pattern; the bytes go through the vault-backed
// /api/persons/:id/photo endpoints.
export function PersonPhoto({
  personId,
  photoPath,
  fullName,
}: {
  personId: number;
  photoPath: string | null;
  fullName: string;
}): JSX.Element {
  const canWrite = can('person.write');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const upload = useUploadPersonPhoto();
  const remove = useDeletePersonPhoto();

  const busy = upload.isPending || remove.isPending;

  const onPick = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    try {
      await upload.mutateAsync({ personId, file });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="person-photo">
      <PersonAvatar personId={personId} photoPath={photoPath} fullName={fullName} size="lg" />
      {canWrite && (
        <div className="person-photo-actions">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {upload.isPending ? 'Uploading…' : photoPath ? 'Replace' : 'Upload photo'}
          </button>
          {photoPath && (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => setConfirmRemove(true)}
            >
              Remove
            </button>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => void onPick(e.target.files?.[0])}
      />
      <ConfirmDialog
        open={confirmRemove}
        title="Remove photo"
        message="Remove this person's photo? This cannot be undone."
        confirmLabel="Remove"
        danger
        busy={remove.isPending}
        onConfirm={() => {
          setError(null);
          remove.mutate(
            { personId },
            {
              onError: (e) => setError(e.message),
              onSettled: () => setConfirmRemove(false),
            },
          );
        }}
        onCancel={() => setConfirmRemove(false)}
      />
      {error && <span className="error-text"> {error}</span>}
    </div>
  );
}
